import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { TagIntelligencePage } from './TagIntelligencePage';
import { PagePanel, PanelBody, PaneledPage } from '@root/Design/Layout';
import { Button, Divider, Group, Modal, Title, Text, Card, Space, Center, useMantineTheme, Box, Alert } from '@mantine/core';
import { AlertTriangle, Circle, Clock, Plus, Settings, Star, Tag } from 'tabler-icons-react';
import { RestrictedTagsList } from '../Components/RestrictedTagsList';
import { useCompany } from '@root/Components/Router/CompanyContent';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { SaveRestrictedTags } from '../Components/SaveRestrictedTags';
import { RestrictedTag } from '@apis/TagManager/model';
import { FillerSwitch } from '@root/Design/Filler';
import { useDi, useDiMemo } from '@root/Services/DI';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { useAuthZValues } from '@root/Services/AuthorizationService';
import { IRestrictedTagCollection, RestrictedTagService } from '@root/Services/Resources/RestrictedTagService';
import { EventEmitter, useEventValue } from '@root/Services/EventEmitter';
import { IRestrictedTagStats, RestrictedTagStatsService } from '@root/Services/Resources/TagStatsService';
import { naiveJsonCopy } from '@root/Services/QueryExpr';

function RestrictedTags() {
    const com = useCompany();
    const notifSvc = useDi(NotificationService);
    const restrictedTagSvc = useDiMemo(RestrictedTagService);
    const tagStatsSvc = useDiMemo(RestrictedTagStatsService);
    const { canEdit } = useAuthZValues({ canEdit: { RestrictedTag: 'Manage' } });
    const theme = useMantineTheme();
    const [openModal, setOpenModal] = useState(false);
    const [hasSaved, setHasSaved] = useState(false);
    const [selectedTag, setSelectedTag] = useState<RestrictedTag>();
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [restrictedTagsList, setRestrictedTagsList] = useState<IRestrictedTagCollection>();
    const tagStatsEvt = useMemo(() => new EventEmitter<IRestrictedTagStats | undefined>(undefined), []);
    const tagStats = useEventValue(tagStatsEvt);

    const handleEdit = (actionType: string, tag: RestrictedTag) => {
        setSelectedTag(naiveJsonCopy(tag));
        if (actionType === 'edit') setOpenEditModal(true);
        else if (actionType === 'remove') setOpenRemoveModal(true);
    };

    useEffect(() => {
        (async () => {
            await loadRestrictedTags();
        })();
    }, []);

    const loadRestrictedTags = async () => {
        setIsLoading(true);
        const restrictedTags = await restrictedTagSvc.getRestrictedTags();
        if (!tagStatsEvt.value) {
            tagStatsSvc.createStats(restrictedTags.getTags()).then(tagStatsEvt.emit);
        }
        setRestrictedTagsList(restrictedTags);
        setIsLoading(false);
    };

    const closeSidePanel = () => {
        setOpenModal(false);
        setOpenEditModal(false);
        setSelectedTag(undefined);
    };

    const removeTagHandler = async (tag: RestrictedTag) => {
        setIsLoading(true);
        try {
            tag.IsActive = false;
            setOpenRemoveModal(false);
            await saveRestrictedTags([tag]);
        } catch {
            notifSvc.notify(
                'Removal Failed!',
                'There was an error removing the prioritized tag. Please contact support. ',
                'error',
                <AlertTriangle />
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (tags: RestrictedTag[]) => {
        setIsLoading(true);
        try {
            saveRestrictedTags(tags);
        } catch {
            notifSvc.notify('Saved Failed!', 'There was an error saving the prioritized tag. Please contact support. ', 'error', <AlertTriangle />);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefresh = useCallback(async () => {
        await restrictedTagSvc.reload().then(setRestrictedTagsList);
    }, [restrictedTagSvc]);

    const saveRestrictedTags = async (tags: RestrictedTag[]) => {
        const savedTags = await restrictedTagSvc.save(tags);
        if (tagStats && restrictedTagsList) {
            tagStats.reloadStats(restrictedTagsList.getValidTags(savedTags)).then(tagStatsEvt.emit);
        }
        closeSidePanel();
        setHasSaved(true);
        await loadRestrictedTags();
    };
    const addRestrictedTagsButton =
        canEdit && !(openModal || openEditModal) ? (
            <Button leftIcon={<i className="ti ti-plus" />} variant="outline" onClick={() => setOpenModal(true)}>
                Add Another Tag
            </Button>
        ) : null;
    return (
        <TagIntelligencePage>
            <PaneledPage>
                <PagePanel size="fill">
                    <PanelBody>
                        <FillerSwitch loading={isLoading}>
                            {() => (
                                <Box sx={{ maxWidth: 950 }}>
                                    <Group position="apart">
                                        <Title order={2}>Prioritized Tags</Title>
                                        {restrictedTagsList!.isEmpty ? null : addRestrictedTagsButton}
                                    </Group>
                                    <Space h="md" />
                                    <Card p="md" withBorder radius="lg">
                                        {restrictedTagsList!.isEmpty ? (
                                            <Box p="md">
                                                <TagConfigurationIntro canAdd={!openModal && canEdit} onAdd={() => setOpenModal(true)} />
                                            </Box>
                                        ) : (
                                            <RestrictedTagsList
                                                readonly={!canEdit}
                                                companyId={com?.Id ?? 0}
                                                onClick={handleEdit}
                                                onRefresh={handleRefresh}
                                                tagStats={tagStats}
                                                restrictedTags={restrictedTagsList!.restrictedTags}
                                            />
                                        )}
                                    </Card>
                                    {!hasSaved ? null : (
                                        <Card p={0} withBorder sx={{ borderColor: theme.colors.warning[6] }} radius="md" m="lg">
                                            <Alert icon={<Clock />} title="Score Change Delay" color="warning" sx={{ borderWidth: 1 }}>
                                                Changes to score options may take up to 24 hours to reflect in the Tag Health Scores.
                                            </Alert>
                                        </Card>
                                    )}
                                </Box>
                            )}
                        </FillerSwitch>
                    </PanelBody>
                </PagePanel>
                {(openModal || openEditModal) && (
                    <Fragment key={selectedTag?.Id}>
                        <Divider color="gray.3" orientation="vertical" />
                        <SaveRestrictedTags
                            pageTitle={!openEditModal ? 'Add a Tag' : canEdit ? 'Edit a Tag' : 'Prioritized Tag'}
                            companyId={com?.Id ?? 0}
                            onDone={closeSidePanel}
                            tagtoUpdate={selectedTag}
                            onSave={handleSave}
                            readonly={!canEdit}
                        ></SaveRestrictedTags>
                    </Fragment>
                )}
            </PaneledPage>
            {/* Remove Tag Modal */}
            <Modal
                opened={openRemoveModal}
                onClose={() => setOpenRemoveModal(false)}
                title={
                    <Text>
                        Remove <strong>{selectedTag?.TagName}</strong> Prioritized Tag
                    </Text>
                }
                centered
            >
                <Text>
                    Are you sure you want to remove <strong>{selectedTag?.TagName}</strong> from the list of prioritized tags?
                </Text>
                <Group position="right" mt="md">
                    <Button variant="default" onClick={() => setOpenRemoveModal(false)}>
                        Cancel
                    </Button>
                    <Button color="error" onClick={() => removeTagHandler(selectedTag!)}>
                        Confirm
                    </Button>
                </Group>
            </Modal>
        </TagIntelligencePage>
    );
}

function TagConfigurationIntro({ onAdd, canAdd }: { onAdd: () => void; canAdd?: boolean }) {
    const { colors } = useMantineTheme();
    return (
        <Center>
            <Group noWrap sx={{ width: 500 }} spacing={20}>
                <Box
                    mb={50}
                    sx={{
                        height: 48,
                        width: 48,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ['svg']: { position: 'absolute' },
                    }}
                >
                    <Tag size={48} strokeWidth={0.5} fill={colors.primary[2]} stroke={colors.primary[6]} />
                    <Circle style={{ marginLeft: -14, marginTop: -14 }} size={8} stroke={colors.primary[6]} strokeWidth={4} fill="#fff" />
                    <Star style={{ marginLeft: 7, marginTop: 5 }} size={16} stroke={colors.primary[6]} strokeWidth={1.75} fill="#fff" />
                </Box>
                <Box>
                    <Text color="dimmed">No Prioritized Tags</Text>
                    <Text size="sm">
                        Prioritized Tags are used to refine <strong>Tag Health Scores</strong> and to signal which tags are{' '}
                        <strong>most important</strong> in your organization.
                    </Text>
                    <Space h="md" />
                    <Button hidden={!canAdd} variant="outline" leftIcon={<Plus size={18} />} onClick={onAdd}>
                        Add Prioritized Tags
                    </Button>
                </Box>
            </Group>
        </Center>
    );
}

endpoint('tag-configuration', RestrictedTags, 'Tag Intelligence');
